import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Typography } from '@mui/material';
import { selectPageInfo, setRows } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { deleteUserClaimMembership } from 'common/services/api/UserClaimMembership/UserClaimMembership.thunk';
import { handleDeleteDialogError } from 'common/utils/errorHandlers';
import { useSnackbar } from 'notistack';

import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { DeleteDialog } from '../../common/components/deleteDialog/DeleteDialog';
import { setDialogOpen } from '../../common/components/deleteDialog/deleteDialogSlice';
import { DeleteDialogContentProps } from '../../common/types/TypeDefs';

export function UserClaimMembershipDeleteDialogContent({
    customDeleteTitle,
    itemId,
    editingRow
}: Readonly<DeleteDialogContentProps<{ claimName: string, claimId: string}>>) {
    const { claimName, claimId } = editingRow ?? {};
    const { enqueueSnackbar } = useSnackbar();
    const [processing, setProcessing] = useState<boolean>(false);
    const params = useParams();
    const { tenantId, userId } = params;
    const dispatch = useAppDispatch();
    const pageInfo = useAppSelector(selectPageInfo);

    const successHandler = () => {
        dispatch(setDialogOpen(false));
        enqueueSnackbar('Claim has been removed from this User under this Tenant.', { variant: 'success' });
    };

    const handleDeleteActionError = (error: { message: string; }) => {
        handleDeleteDialogError('User from this Claim', enqueueSnackbar, extractStatusCode(error.message));
        setProcessing(false);
    };

    const claimDeleteCallback = useCallback(() => {
        if (itemId) {
            setProcessing(true);
            dispatch(deleteUserClaimMembership({
                tenantId: tenantId ?? '',
                userId: userId ?? '',
                claimId: claimId ?? ''
            }))
                .unwrap()
                .then((result) => {
                    dispatch(setRows(result.rows));
                    successHandler();
                })
                .catch(handleDeleteActionError)
                .finally(()=> {
                    setProcessing(false);
                });
        }
    }, [itemId]);

    return  (
        <DeleteDialog deleteCallback={claimDeleteCallback} processing={processing ?? false} customDeleteTitle={customDeleteTitle}>
            <Typography color="info.light" fontSize='0.9rem' fontWeight='550'>
                User: <Typography component="span" color="text.primary" fontWeight="400">{`${pageInfo?.firstName} ${pageInfo?.lastName}`}</Typography>
            </Typography>
            <Typography color="info.light" fontSize='0.9rem' fontWeight='550'>
                Claim Name: <Typography component="span" color="text.primary" fontWeight="400">{claimName}</Typography>
            </Typography>
            <Typography color="info.light" fontSize='0.9rem' fontWeight='550'>
                Tenant Name: <Typography component="span" color="text.primary" fontWeight="400">{pageInfo?.name}</Typography>
            </Typography>
        </DeleteDialog>
    );
}
